import { useEffect, useState } from 'react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { ChevronDown, ChevronRight } from 'lucide-react'
import { DecisionCard } from './DecisionCard'
import { urgencyVariant } from '@/lib/decision-utils'
import type { DecisionCluster as Cluster } from '@/lib/decision-utils'

export interface ClusterSelection {
  isSelected: (id: string) => boolean
  toggle: (id: string) => void
  setMany: (ids: string[], checked: boolean) => void
  allSelected: (ids: string[]) => boolean
  someSelected: (ids: string[]) => boolean
}

interface DecisionClusterProps {
  cluster: Cluster
  focusedId: string | null
  onFocus: (id: string) => void
  onApprove: (id: string) => void
  onReject: (id: string) => void
  approving: boolean
  rejecting: boolean
  onApproveAll: (ids: string[]) => void
  onRejectAll: (ids: string[]) => void
  batchPending: boolean
  exitingIds?: Set<string>
  selection: ClusterSelection
  /** Force the cluster open (e.g. a deep-linked decision lives inside it). */
  forceExpanded?: boolean
}

type Armed = 'none' | 'approve' | 'reject'

/**
 * Renders a near-identical group of decisions. A singleton renders as a plain
 * card; a true cluster (count > 1) collapses to a single header row with a
 * "×N" badge, a select-all checkbox, two-step batch buttons, and an expand
 * toggle that reveals the individual cards.
 */
export function DecisionCluster({
  cluster,
  focusedId,
  onFocus,
  onApprove,
  onReject,
  approving,
  rejecting,
  onApproveAll,
  onRejectAll,
  batchPending,
  exitingIds,
  selection,
  forceExpanded,
}: DecisionClusterProps) {
  const [expanded, setExpanded] = useState(false)
  const [armed, setArmed] = useState<Armed>('none')

  // Open the cluster whenever a deep-link points inside it.
  useEffect(() => {
    if (forceExpanded) setExpanded(true)
  }, [forceExpanded])

  function fire(action: 'approve' | 'reject') {
    if (armed !== action) {
      setArmed(action)
      return
    }
    if (action === 'approve') onApproveAll(cluster.pendingIds)
    else onRejectAll(cluster.pendingIds)
    setArmed('none')
  }

  const card = (d: typeof cluster.decisions[number], index: number) => (
    <DecisionCard
      key={d.id}
      decision={d}
      index={index}
      focused={focusedId === d.id}
      exiting={exitingIds?.has(d.id)}
      onFocus={onFocus}
      onApprove={onApprove}
      onReject={onReject}
      approving={approving}
      rejecting={rejecting}
      selectable
      selected={selection.isSelected(d.id)}
      onToggleSelect={selection.toggle}
    />
  )

  if (!cluster.isCluster) {
    return card(cluster.decisions[0], 0)
  }

  const pendingCount = cluster.pendingIds.length
  const hasPending = pendingCount > 0
  const allSel = selection.allSelected(cluster.pendingIds)
  const someSel = selection.someSelected(cluster.pendingIds)

  return (
    <div className="bg-cmd-container">
      {/* Cluster header */}
      <div className="flex flex-wrap items-center gap-2 p-3">
        {hasPending && (
          <Checkbox
            checked={allSel}
            indeterminate={someSel}
            onCheckedChange={(c) => selection.setMany(cluster.pendingIds, c)}
            aria-label={`Select all ${pendingCount} in this group`}
          />
        )}
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="flex min-w-0 flex-1 items-center gap-2 text-left"
          aria-expanded={expanded}
        >
          {expanded ? (
            <ChevronDown className="h-4 w-4 shrink-0 text-cmd-outline" aria-hidden="true" />
          ) : (
            <ChevronRight className="h-4 w-4 shrink-0 text-cmd-outline" aria-hidden="true" />
          )}
          <span className="truncate text-sm font-medium text-white/90">{cluster.summary}</span>
          <Badge variant={urgencyVariant(cluster.decisions[0].urgency)} className="shrink-0">
            ×{cluster.decisions.length}
          </Badge>
          <span className="shrink-0 font-mono text-[0.6rem] text-cmd-outline">
            {cluster.decisions[0].skillName}
          </span>
        </button>

        {hasPending && (
          <div className="flex items-center gap-1">
            {armed !== 'none' && (
              <Button
                size="sm"
                variant="ghost"
                disabled={batchPending}
                onClick={() => setArmed('none')}
                className="font-label text-[0.65rem] tracking-wider"
              >
                CANCEL
              </Button>
            )}
            <Button
              size="sm"
              variant="success"
              disabled={batchPending}
              onClick={() => fire('approve')}
              className="font-label text-[0.65rem] tracking-wider"
            >
              {armed === 'approve' ? `CONFIRM · APPROVE ${pendingCount}` : `APPROVE ${pendingCount}`}
            </Button>
            <Button
              size="sm"
              variant="destructive"
              disabled={batchPending}
              onClick={() => fire('reject')}
              className="font-label text-[0.65rem] tracking-wider"
            >
              {armed === 'reject' ? `CONFIRM · REJECT ${pendingCount}` : `REJECT ${pendingCount}`}
            </Button>
          </div>
        )}
      </div>

      {/* Expanded individual cards */}
      {expanded && <div className="space-y-1 pl-3">{cluster.decisions.map((d, i) => card(d, i))}</div>}
    </div>
  )
}
